// Detects Next.js / Turbopack dynamic-import chunk load failures and triggers a
// one-time recovery reload.
//
// These happen when a new build is deployed and a client still running the
// previous build requests a now-deleted, content-hashed chunk
// (`/_next/static/chunks/<hash>.js` -> 404 -> ChunkLoadError). The chunk no
// longer exists on the origin, so the only recovery is a hard reload, which
// fetches the fresh HTML and the new chunk manifest.

const CHUNK_ERROR_PATTERNS = [
    "ChunkLoadError",
    "Loading chunk",
    "Failed to load chunk",
    "Loading CSS chunk",
    "error loading dynamically imported module",
    "Importing a module script failed",
];

export function isChunkLoadError(error: unknown): boolean {
    if (!error) return false;

    if (typeof error === "object" && error !== null && "name" in error) {
        if (
            String((error as { name?: unknown }).name ?? "") ===
            "ChunkLoadError"
        ) {
            return true;
        }
    }

    const message =
        error instanceof Error
            ? error.message
            : typeof error === "string"
              ? error
              : typeof error === "object" &&
                  error !== null &&
                  "message" in error
                ? String((error as { message?: unknown }).message ?? "")
                : "";

    return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

// sessionStorage guard so a genuinely broken deploy can't trap the user in a
// reload loop: we allow one automatic reload per browser session. If we've
// already reloaded once, we let the error surface to the UI instead.
const RELOAD_FLAG = "sc:chunk-reload";

/**
 * Reloads the page once per session in response to a chunk load failure.
 * Returns true if a reload was triggered, false if we've already reloaded this
 * session (in which case the caller should fall back to showing an error UI).
 */
export function reloadOnceForChunkError(): boolean {
    if (typeof window === "undefined") return false;

    try {
        if (window.sessionStorage.getItem(RELOAD_FLAG)) {
            return false;
        }
        window.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
    } catch {
        // sessionStorage unavailable (private mode / quota). Reload anyway —
        // accepting the small loop risk over swallowing a recoverable error.
    }

    window.location.reload();
    return true;
}
